<template>
  <ts-card>
    <div
      class="form-header ts-bg-gray-100 ts-border-b ts-border-gray-400 ts-p-4 ts-relative"
    >
      <h4 class="ts-text-sm">{{ $t('approvalRequestFlow.pageTitle') }}</h4>
      <ts-modal-close-button />
    </div>
    <ts-loading-banner :loading="loading">
      <ts-card-wrapper>
        <div class="row">
          <div class="col-lg-5">
            <div class="form-group">
              <label class="col-form-label required">{{
                $t('approvalRequestFlow.approvalType')
              }}</label>
              <select
                class="form-control"
                v-model.number="model.app_type_id"
                @change="fetchParam"
                :class="{ 'is-invalid': errors.has('app_type_id') }"
              >
                <option value>{{ $t('select') }}</option>
                <option
                  v-for="(app_type, index) in approvalTypes"
                  :key="index"
                  :value="app_type.not_type_id"
                  >{{ app_type.not_types }}</option
                >
              </select>
              <div class="invalid-feedback" v-if="errors.has('app_type_id')">
                {{ errors.first('app_type_id') }}
              </div>
            </div>
            <div class="row form-group">
              <div class="col-lg-6">
                <label class="col-form-label">
                  {{ $t('approvalRequestFlow.escalateAfterHours') }}
                </label>
                <input
                  type="number"
                  class="form-control"
                  v-model.number="model.escalate_after_xx_hours"
                  min="0"
                />
              </div>
              <div class="col-lg-6">
                <label class="col-form-label">
                  {{ $t('approvalRequestFlow.escalateTimes') }}
                </label>
                <input
                  type="number"
                  class="form-control"
                  v-model.number="model.escalate_times"
                  min="0"
                />
              </div>
            </div>
            <div class="form-group">
              <label class="col-form-label">
                {{ $t('approvalRequestFlow.approverNotificationTemplate') }}
              </label>
              <textarea
                rows="2"
                class="form-control"
                v-model="model.approver_notification_template"
              ></textarea>
            </div>
            <div class="form-group">
              <label class="col-form-label">
                {{ $t('approvalRequestFlow.initiatorNotificationTemplate') }}
              </label>
              <textarea
                rows="3"
                class="form-control"
                v-model="model.initiator_notification_template"
              ></textarea>
            </div>
          </div>
          <div class="col-lg-4">
            <div class="form-group">
              <label class="col-form-label">
                {{ $t('approvalRequestFlow.emailSubject') }}
              </label>
              <input
                type="text"
                class="form-control"
                v-model="model.email_subject"
              />
            </div>
            <div class="form-group">
              <label class="col-form-label">
                {{ $t('approvalRequestFlow.approverEmailTemplate') }}
              </label>
              <textarea
                style="height: 130px; margin-top: 0px; margin-bottom: 0px;"
                class="form-control"
                v-model="model.approver_email_template"
              ></textarea>
            </div>
            <div class="form-group">
              <label class="col-form-label">
                {{ $t('approvalRequestFlow.initiatorEmailTemplate') }}
              </label>
              <textarea
                rows="3"
                class="form-control"
                v-model="model.initiator_email_template"
              ></textarea>
            </div>
          </div>
          <div class="col-lg-3">
            <div class="note note-primary note-border-top">
              <div class="note-content text-center">
                <h4>
                  <b>
                    {{ $t('approvalRequestFlow.parameters') }}
                    <i class="fas fa-spinner spin" v-if="loadParameter"></i>
                  </b>
                  <i
                    class="fas fa-question-circle ml-2"
                    v-tooltip="
                      $t(
                        'approvalRequestFlow.useTheseParametersToCreateTemplate'
                      )
                    "
                    v-if="!loadParameter"
                  ></i>
                </h4>
                <div class="m-auto">
                  <div v-for="(param, index) in parameters" :key="index">
                    <ul>
                      <li
                        class="parameter"
                        v-clipboard:copy="param.parameter"
                        v-clipboard:success="onCopy"
                        v-clipboard:error="onError"
                        v-tooltip="copied"
                      >
                        {{ param.parameter }}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <fieldset>
          <legend class="text-black">
            {{ $t('approvalRequestFlow.approveLevel') }}
          </legend>
          <Details v-model="model.details" />
        </fieldset>
        <div class="row">
          <div class="col-md-12 ts-mt-3 text-right">
            <ts-button class="mr-2" @click.prevent="() => $emit('close')">
              {{ $t('cancel') }}
            </ts-button>
            <ts-button
              v-if="!isUpdate"
              class="mr-2"
              color="primary"
              outline
              @click.prevent="onSaveAddNew"
              :waiting="waiting_new"
              >{{ $t('save_add_new') }}</ts-button
            >
            <ts-button
              v-if="!isUpdate"
              color="primary"
              @click.prevent="onSave"
              :waiting="waiting"
              >{{ $t('save') }}</ts-button
            >
            <ts-button
              v-if="isUpdate"
              color="primary"
              @click.prevent="onUpdate"
              :waiting="waiting"
              >{{ $t('update') }}</ts-button
            >
          </div>
        </div>
      </ts-card-wrapper>
    </ts-loading-banner>
  </ts-card>
</template>

<script>
import { mapState } from 'vuex'
import { Errors } from 'form-backend-validation'
import Details from './details.vue'

export default {
  name: 'create-form',
  components: { Details },
  data () {
    return {
      loading: false,
      waiting_new: false,
      waiting: false,
      loadParameter: false,
      errors: new Errors(),
      copied: this.$t('approvalRequestFlow.copyParameters'),
      model: {
        app_flow_id: 0,
        app_type_id: '',
        escalate_after_xx_hours: '',
        is_enabled: true,
        escalate_times: '',
        approver_notification_template: '',
        initiator_notification_template: '',
        email_subject: '',
        approver_email_template: '',
        initiator_email_template: '',
        details: []
      }
    }
  },
  created () {
    this.fetchResource()
    this.setEditData()
  },
  computed: {
    ...mapState('setting/approvalRequestFlow', [
      'edit_data',
      'approvalTypes',
      'parameters'
    ]),
    isUpdate () {
      return !_.isEmpty(this.edit_data)
    }
  },
  methods: {
    fetchResource () {
      this.loading = true
      this.$store
        .dispatch('setting/approvalRequestFlow/fetchResource')
        .then(() => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    fetchParam () {
      this.loadParameter = true
      this.$store
        .dispatch(
          'setting/approvalRequestFlow/fetchParameter',
          this.model.app_type_id
        )
        .then(() => {
          this.loadParameter = false
        })
        .catch(error => {
          this.loadParameter = false
          this.$toasted.error(error.message)
        })
    },
    onCopy: function (e) {
      this.copied = this.$t('approvalRequestFlow.copied')
      setTimeout(() => {
        this.copied = this.$t('approvalRequestFlow.copyParameters')
      }, 1000)
    },
    onError: function (e) {
      this.copied = this.$t('approvalRequestFlow.failedCopied')
    },
    onSave () {
      this.waiting = true
      this.errors = new Errors()
      this.$store
        .dispatch('setting/approvalRequestFlow/store', this.model)
        .then(response => {
          this.$toasted.success(response.message)
          this.$emit('close')
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    onSaveAddNew () {
      this.waiting_new = true
      this.errors = new Errors()
      this.$store
        .dispatch('setting/approvalRequestFlow/store', this.model)
        .then(response => {
          this.$toasted.success(response.message)
          this.clearInput()
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting_new = false
        })
    },
    onUpdate () {
      this.waiting = true
      this.errors = new Errors()
      this.$store
        .dispatch('setting/approvalRequestFlow/update', {
          id: this.edit_data.app_flow_id,
          data: this.model
        })
        .then(response => {
          this.$toasted.success(response.message)
          this.$emit('close')
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    clearInput () {
      this.model = {
        app_flow_id: 0,
        app_type_id: '',
        escalate_after_xx_hours: '',
        is_enabled: true,
        escalate_times: '',
        approver_notification_template: '',
        initiator_notification_template: '',
        email_subject: '',
        approver_email_template: '',
        initiator_email_template: '',
        details: []
      }
    },
    async setEditData () {
      if (!_.isEmpty(this.edit_data)) {
        this.model.app_flow_id = this.edit_data.app_flow_id
        this.model.app_type_id = this.edit_data.app_type_id
        this.model.escalate_after_xx_hours = this.edit_data.escalate_after_xx_hours
        this.model.escalate_times = this.edit_data.escalate_times
        this.model.approver_notification_template = this.edit_data.approver_notification_template
        this.model.initiator_notification_template = this.edit_data.initiator_notification_template
        this.model.email_subject = this.edit_data.email_subject
        this.model.approver_email_template = this.edit_data.approver_email_template
        this.model.initiator_email_template = this.edit_data.initiator_email_template
        this.model.is_enabled = this.edit_data.is_enabled
        this.model.details = this.edit_data.details.map(el => ({
          id: el.id,
          level_order: el.level_order,
          approver_type_id: el.approver_type_id,
          approver_type_value: el.approver_type_value,
          approver_type_cc_id: el.approver_type_cc_id,
          approver_type_cc_value: el.approver_type_cc_value
        }))
        this.fetchParam()
      }
    }
  }
}
</script>

<style lang="css" scoped>
.ant-modal {
  font-size: 12px !important;
}
.ant-modal-body {
  font-size: 12px;
}
.parameter {
  cursor: pointer;
  text-align: center;
}
.note-border-top {
  border-top: 3px solid #348fe2;
  border-left: none;
}
li {
  padding: 2px 0;
}
</style>
